import React, { FC } from 'react';

import { GrafanaTheme2 } from '@grafana/data';
import { useStyles2 } from '@grafana/ui';

import { css } from '@emotion/css';

import { Analysis, AnalysisDisplayStatus } from 'types';

import AnalysisIcon from './AnalysisIcon';

// Component Props Interface
interface Props {
  analysis: Analysis;
  onSelection: (analysisId: string) => void;
}

// Declare Component
export const Card: FC<Props> = ({ analysis, onSelection }) => {
  const styles = useStyles2(getStyles);

  const displayName = analysis.title === '' || analysis.title === null ? analysis.name : analysis.title;
  return (
    <div
      className={getStyleByStatus(analysis.displayStatus, styles)}
      onClick={() => {
        onSelection(analysis.id);
      }}
    >
      <div className={styles.contentHeading}>
        <div className={styles.analysisTitle}>{displayName}</div>
        <AnalysisIcon displayStatus={analysis.displayStatus} />
      </div>
      <div className={styles.message}>{analysis.result.message}</div>
    </div>
  );
};

function getStyleByStatus(status: AnalysisDisplayStatus, styles: any) {
  switch (status) {
    case 'interesting':
      return styles.interesting;
    case 'passed':
      return styles.passed;
    case 'failed':
      return styles.failed;
    case 'running':
      return styles.running;
    case 'skipped':
      return styles.skipped;
    default:
      return styles.unknown;
  }
}

// Define CSS Styles
const getStyles = (theme: GrafanaTheme2) => {
  const baseStyle = css`
    flex: 1 1 350px;
    width: 350px;
    max-width: 350px;
    height: 180px;
    padding: 20px;
    border-radius: 3px;
    cursor: pointer;
  `;

  return {
    contentHeading: css`
      display: flex;
      justify-content: space-between;
      align-items: center;
      color: ${theme.colors.text.primary};
      overflow: hidden;
    `,
    message: css`
      color: ${theme.colors.text.secondary};
      margin-top: 10px;
      margin-bottom: 10px;
      max-height: 80%;
      overflow: hidden;
    `,
    interesting: css`
      ${baseStyle}
      background-color: ${theme.colors.background.secondary};
      border-top: 1px solid rgb(255, 120, 10);
    `,
    passed: css`
      ${baseStyle}
      background-color: ${theme.colors.background.secondary};
      border-top: 1px solid rgb(86, 166, 75);
    `,
    failed: css`
      ${baseStyle}
      background-color: ${theme.colors.background.secondary};
      border-top: 1px solid rgb(224, 47, 68);
    `,
    running: css`
      ${baseStyle}
      background-color: ${theme.colors.background.secondary};
      border-top: 1px solid rgb(50, 116, 217);
    `,
    skipped: css`
      ${baseStyle}
      background-color: ${theme.colors.background.secondary};
      border-top: 1px solid ${theme.colors.text.maxContrast};
    `,
    unknown: css`
      ${baseStyle}
      background-color: ${theme.colors.background.secondary};
      border-top: 1px solid rgb(111, 111, 111);
    `,
    analysisTitle: css`
      font-size: 14px;
      font-weight: 500;
      white-space: nowrap;
      overflow: ellipsis;
      text-overflow: ellipsis;
      overflow-y: hidden;
    `,
  };
};

// Export Component
export default Card;
